import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Building2, CalendarCheck, Wrench, Package,
  Users, BarChart3, MessageSquare, Settings, FileText, Star,
  CreditCard, Shield, Bike, ClipboardList, ChevronRight, Home, MapPin, Globe, KanbanSquare
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const NAV_ITEMS = {
  superadmin: [
    { label: 'SA Dashboard', icon: Shield, path: '/super-admin' },
    { label: 'Tenants', icon: Building2, path: '/super-admin/tenants' },
    { label: 'Channel Manager', icon: Globe, path: '/super-admin/channel-manager' },
  ],
  management: [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { label: 'Properties', icon: Building2, path: '/properties' },
    { label: 'Calendar', icon: CalendarCheck, path: '/calendar' },
    { label: 'Bookings', icon: KanbanSquare, path: '/bookings', subItems: [
      { label: 'All Bookings', path: '/bookings' },
      { label: 'Pipeline', path: '/bookings/pipeline' },
    ]},
    { label: 'Channel Manager', icon: Globe, path: '/channel-manager' },
    { label: 'Equipment', icon: Bike, path: '/equipment', subItems: [
      { label: 'Catalog', path: '/equipment' },
      { label: 'Bookings', path: '/equipment/bookings' },
      { label: 'Operations', path: '/equipment/operations' },
    ]},
    { label: 'CRM / Leads', icon: MessageSquare, path: '/crm' },
    { label: 'Maintenance', icon: Wrench, path: '/maintenance' },
    { label: 'Content', icon: MapPin, path: '/content' },
    { label: 'Reports', icon: BarChart3, path: '/reports' },
    { label: 'Users', icon: Users, path: '/users' },
    { label: 'Billing', icon: CreditCard, path: '/billing' },
    { label: 'Settings', icon: Settings, path: '/settings' },
  ],
  guest: [
    { label: 'My Stays', icon: Home, path: '/guest/dashboard' },
    { label: 'My Bookings', icon: CalendarCheck, path: '/guest/bookings' },
    { label: 'Profile', icon: Settings, path: '/profile' },
  ]
};

const NavItem = ({ item, collapsed }) => {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  const isActive = location.pathname === item.path || 
    (item.subItems && item.subItems.some(s => location.pathname === s.path));

  if (item.subItems) {
    return (
      <div>
        <button
          onClick={() => setOpen(p => !p)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            padding: collapsed ? '10px 0' : '10px 12px', borderRadius: 8,
            background: isActive ? 'rgba(59,130,246,0.1)' : 'transparent',
            color: isActive ? '#3B82F6' : '#6B7280',
            border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500,
            justifyContent: collapsed ? 'center' : 'space-between',
            transition: 'all 0.15s'
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <item.icon size={18} />
            {!collapsed && item.label}
          </span>
          {!collapsed && <ChevronRight size={14} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />}
        </button>
        {open && !collapsed && (
          <div style={{ marginLeft: 28, marginTop: 2 }}>
            {item.subItems.map(sub => (
              <NavLink key={sub.path} to={sub.path} end style={({ isActive }) => ({
                display: 'block', padding: '8px 12px', borderRadius: 6, fontSize: 13,
                color: isActive ? '#3B82F6' : '#6B7280', fontWeight: isActive ? 500 : 400,
                textDecoration: 'none', background: isActive ? 'rgba(59,130,246,0.08)' : 'transparent'
              })}>
                {sub.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink to={item.path} end style={({ isActive }) => ({
      display: 'flex', alignItems: 'center', gap: 10,
      padding: collapsed ? '10px 0' : '10px 12px', borderRadius: 8,
      background: isActive ? 'rgba(59,130,246,0.1)' : 'transparent',
      color: isActive ? '#3B82F6' : '#6B7280',
      textDecoration: 'none', fontSize: 14, fontWeight: isActive ? 600 : 500,
      transition: 'all 0.15s', justifyContent: collapsed ? 'center' : 'flex-start'
    })}>
      <item.icon size={18} />
      {!collapsed && item.label}
    </NavLink>
  );
};

const Sidebar = ({ collapsed, mobileOpen, onMobileClose, isGuest }) => {
  const { user, isSuperAdmin } = useAuth();

  const items = isSuperAdmin
    ? NAV_ITEMS.superadmin
    : isGuest
      ? NAV_ITEMS.guest
      : NAV_ITEMS.management;

  const sidebarStyle = {
    position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 200,
    width: collapsed ? 72 : 256, background: '#fff',
    borderRight: '1px solid #E5E7EB',
    display: 'flex', flexDirection: 'column',
    transition: 'width 0.3s ease',
    overflowX: 'hidden',
    overflowY: 'auto'
  };

  const mobileSidebarStyle = {
    ...sidebarStyle,
    width: 256,
    transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.3s ease',
    boxShadow: '4px 0 20px rgba(0,0,0,0.1)'
  };

  const isMobile = window.innerWidth < 768;

  return (
    <nav style={isMobile ? mobileSidebarStyle : sidebarStyle}>
      {/* Logo */}
      <div style={{
        padding: collapsed ? '20px 0' : '20px 20px',
        borderBottom: '1px solid #F3F4F6',
        display: 'flex', alignItems: 'center', gap: 10,
        justifyContent: collapsed ? 'center' : 'flex-start',
        minHeight: 64, flexShrink: 0
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0
        }}>
          <Building2 size={18} color="#fff" />
        </div>
        {!collapsed && (
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>Winggs</div>
            <div style={{ fontSize: 11, color: '#6B7280' }}>Rental CRM</div>
          </div>
        )}
      </div>

      {/* User info */}
      {!collapsed && user && (
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="avatar avatar-sm" style={{ background: '#EFF6FF', color: '#3B82F6' }}>
              {user.firstName?.[0]}{user.lastName?.[0]}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.firstName} {user.lastName}
              </div>
              <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'capitalize' }}>
                {user.role?.replace('_', ' ')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purple horizontal line */}
      <div style={{ height: 1, background: 'linear-gradient(90deg, #8B5CF6, transparent)', margin: '0 16px' }} />

      {/* Nav items */}
      <div style={{ flex: 1, padding: collapsed ? '12px 8px' : '12px 10px', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
        {items.map(item => (
          <NavItem key={item.path} item={item} collapsed={collapsed} />
        ))}
      </div>

      {/* Bottom plan badge */}
      {!collapsed && user?.tenantId && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid #F3F4F6', flexShrink: 0 }}>
          <div style={{
            background: '#EFF6FF', borderRadius: 8, padding: '8px 12px',
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            <Star size={14} color="#3B82F6" fill="#3B82F6" />
            <span style={{ fontSize: 12, color: '#3B82F6', fontWeight: 500 }}>
              {user.tenantId?.subscription?.plan?.toUpperCase() || 'BASIC'} Plan
            </span>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Sidebar;
